/**
 * @file     useVowGate.tsx
 * @purpose  The one way a locked VOW value opens VowAccessModal: the account
 *           step signed out, the phone and terms step signed in, a gate_shown
 *           event naming what was pressed, and the page-level
 *           VowMarketAccessProvider modal when one owns the page.
 */
'use client';

import { useCallback, useState, type ComponentType, type ReactNode } from 'react';
import type { ListingEventProps } from '@/core/analytics/listingEvents';
import { useListingEvents } from '@/hooks/useListingEvents';
import { useAuth } from '@/hooks/useAuth';
import { useOptionalVowMarketAccess } from '@/components/vow/vowMarketAccessContext';

// Loaded with the first gate open: every listing card calls this hook, so a
// static import put the modal's form, auth buttons and MUI Dialog in the first
// load of every page with a card grid.
type VowAccessModalType = typeof import('@/components/vow/VowAccessModal').VowAccessModal;
let loadedModal: VowAccessModalType | null = null;
const loadModal = () =>
  import('@/components/vow/VowAccessModal').then((m) => {
    loadedModal = m.VowAccessModal;
    return m.VowAccessModal;
  });

type GateShown = ListingEventProps['gate_shown'];

export interface VowGateOptions {
  /** Server-known sign-in state when the caller has it; the client session otherwise. */
  isAuthenticated?: boolean;
  needsReactivation?: boolean;
  surface?: GateShown['surface'];
  listingKey?: string;
  headline?: string;
}

export interface VowGate {
  authed: boolean;
  openGate: (source: 'teaser_row' | 'cta', field?: string) => void;
  /** The modal, when no page-level VowMarketAccessProvider owns one. */
  modal: ReactNode;
}

export function useVowGate({ isAuthenticated, needsReactivation = false, surface = 'page', listingKey, headline }: VowGateOptions = {}): VowGate {
  const auth = useAuth();
  const authed = isAuthenticated ?? auth.isAuthenticated;
  const openUnlock = useOptionalVowMarketAccess()?.openUnlock;
  const track = useListingEvents();
  const [open, setOpen] = useState(false);
  // A grid of cards would otherwise mount one modal per card before anyone asks.
  const [mounted, setMounted] = useState(false);
  const [Modal, setModal] = useState<ComponentType<Parameters<VowAccessModalType>[0]> | null>(() => loadedModal);

  const openGate = useCallback(
    (source: 'teaser_row' | 'cta', field?: string) => {
      track('gate_shown', { surface, listing_key: listingKey, reason: 'vow_terms', source, field });
      if (openUnlock) return openUnlock();
      if (!loadedModal) void loadModal().then((m) => setModal(() => m));
      setMounted(true);
      setOpen(true);
    },
    [track, surface, listingKey, openUnlock],
  );

  const modal = openUnlock || !mounted || !Modal ? null : (
    <Modal open={open} onClose={() => setOpen(false)} authed={authed} needsReactivation={needsReactivation} headline={headline} />
  );
  return { authed, openGate, modal };
}
